import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Flame, Trophy, ChevronRight, Loader2, Radio, UserPlus, UserCheck, Hash } from "lucide-react";

/**
 * TrendingSidebar — desktop-only widget (xl+). Two variants:
 *   side="left"  → 🔥 Top Creators + 📡 Live Now mini-strip
 *   side="right" → 🏆 Top Gifters
 *
 * All rows link to profile; Top Creators rows include a Follow toggle so
 * users can grow their network without leaving the reels feed.
 */
export function TrendingSidebar({ side = "left", variant = "sidebar" }) {
  const [rows, setRows] = useState([]);
  const [live, setLive] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [following, setFollowing] = useState({});   // { userId: boolean }
  const [busy, setBusy] = useState({});             // { userId: true }

  useEffect(() => {
    let cancel = false;
    const type = side === "right" ? "gifters" : "streamers";
    const load = async () => {
      try {
        // Leaderboard (with all-time fallback)
        let items = [];
        try {
          const r = await api.get(`/leaderboard?type=${type}&period=7d&limit=8`);
          items = r.data?.items || [];
          if (!items.length) {
            const r2 = await api.get(`/leaderboard?type=${type}&period=all&limit=8`);
            items = r2.data?.items || [];
          }
        } catch { /* ignore */ }
        // Live streams (only on left sidebar)
        let liveRows = [];
        if (side === "left") {
          try {
            const r = await api.get(`/live/active`);
            liveRows = (r.data || []).slice(0, 4);
          } catch { /* ignore */ }
        }
        // Trending hashtags (only on right sidebar)
        let tagRows = [];
        if (side === "right") {
          try {
            const r = await api.get(`/hashtags/trending`);
            tagRows = (r.data?.hashtags || []).slice(0, 14);
          } catch { /* ignore */ }
        }
        // Current user (once) so we can compare to determine follow states
        let currentMe = me;
        if (!currentMe) {
          try {
            const meRes = await api.get(`/auth/me`);
            currentMe = meRes.data || null;
          } catch { /* not logged in — sidebar is still useful */ }
        }
        if (cancel) return;
        setRows(items); setLive(liveRows); setTags(tagRows); setMe(currentMe); setLoading(false);
        // Prime the following state — best-effort: batch by asking /users/{id}
        // (already cached in browser) to read `is_following`.
        if (currentMe && items.length && side === "left") {
          const map = {};
          await Promise.all(items.map(async row => {
            const uid = row.user?.id;
            if (!uid || uid === currentMe.id) return;
            try {
              const u = await api.get(`/users/${uid}`);
              map[uid] = !!u.data?.is_following;
            } catch { /* ignore */ }
          }));
          if (!cancel) setFollowing(prev => ({ ...prev, ...map }));
        }
      } catch { /* swallow */ }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancel = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [side]);

  const toggleFollow = async (e, u) => {
    e.preventDefault(); e.stopPropagation();
    if (!u?.id || !me || busy[u.id]) return;
    setBusy(b => ({ ...b, [u.id]: true }));
    const prev = following[u.id];
    setFollowing(f => ({ ...f, [u.id]: !prev }));   // optimistic
    try { await api.post(`/users/${u.id}/follow`); }
    catch { setFollowing(f => ({ ...f, [u.id]: prev })); }
    finally { setBusy(b => ({ ...b, [u.id]: false })); }
  };

  const title = side === "right" ? "Top Gifters" : "Top Creators";
  const Icon  = side === "right" ? Trophy : Flame;
  const color = side === "right" ? "#FFD700" : "#FF2A5F";
  // sidebar → fixed xl+ desktop only. inline → normal flow (used on /trending for mobile users).
  const posClass = variant === "inline"
    ? "block w-full"
    : side === "right"
      ? "hidden xl:block fixed right-4 top-24 z-30 w-64"
      : "hidden xl:block fixed left-4 top-24 z-30 w-64";

  return (
    <aside className={`${posClass} max-h-[calc(100dvh-8rem)] overflow-y-auto scrollbar-hide space-y-3`} data-testid={`trending-sidebar-${side}`}>
      {/* Leaderboard card */}
      <div className="rounded-2xl bg-gradient-to-br from-black/70 to-black/40 backdrop-blur border border-white/10 p-3 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: color + "22", border: `1px solid ${color}55` }}>
            <Icon className="w-3.5 h-3.5" style={{ color }}/>
          </div>
          <div className="flex-1">
            <div className="font-outfit font-extrabold text-sm leading-none" style={{ color }}>{title}</div>
            <div className="text-[9px] text-white/50 mt-0.5">Last 7 days</div>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-5"><Loader2 className="w-4 h-4 animate-spin text-white/40"/></div>
        ) : rows.length === 0 ? (
          <div className="text-[11px] text-white/50 text-center py-4">No data yet</div>
        ) : (
          <div className="space-y-1.5">
            {rows.slice(0, 8).map(r => {
              const uid = r.user?.id;
              const isSelf = uid && me?.id === uid;
              const isFollowing = uid ? following[uid] : false;
              const showFollow = side === "left" && me && uid && !isSelf;
              return (
                <Link key={uid || r.rank}
                  to={uid ? `/u/${uid}` : "/"}
                  data-testid={`trend-row-${r.rank}`}
                  className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-white/8 active:scale-[0.98] transition">
                  <span className={`text-[10px] font-extrabold w-4 text-center ${r.rank <= 3 ? "text-[#FF7A00]" : "text-white/50"}`}>{r.rank}</span>
                  <img src={r.user?.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold truncate">{r.user?.name || r.user?.username || "user"}</div>
                    <div className="text-[9px] text-white/50 truncate">{r.label || `${r.score} pts`}</div>
                  </div>
                  {showFollow ? (
                    <button onClick={(e) => toggleFollow(e, r.user)} disabled={!!busy[uid]}
                      data-testid={`trend-follow-${uid}`}
                      className={`text-[10px] font-extrabold px-2 py-1 rounded-full flex items-center gap-0.5 disabled:opacity-50 active:scale-90 transition ${isFollowing ? "bg-white/10 text-white/80" : "bg-[#00E5FF] text-black"}`}>
                      {isFollowing ? <UserCheck className="w-2.5 h-2.5"/> : <UserPlus className="w-2.5 h-2.5"/>}
                      {isFollowing ? "" : ""}
                    </button>
                  ) : (
                    <ChevronRight className="w-3 h-3 text-white/30 flex-shrink-0"/>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Live Now mini-strip — only on left sidebar */}
      {side === "left" && live.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-[#FF2A5F]/12 to-black/40 backdrop-blur border border-[#FF2A5F]/30 p-3" data-testid="sidebar-live-now">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#FF2A5F]/20 border border-[#FF2A5F]/50">
              <Radio className="w-3.5 h-3.5 text-[#FF2A5F] animate-pulse"/>
            </div>
            <div className="flex-1">
              <div className="font-outfit font-extrabold text-sm leading-none text-[#FF2A5F]">Live Now</div>
              <div className="text-[9px] text-white/50 mt-0.5">{live.length} broadcasting</div>
            </div>
          </div>
          <div className="space-y-1.5">
            {live.map(s => (
              <Link key={s.id} to={`/live/${s.id}`} data-testid={`sidebar-live-${s.id}`}
                className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-white/8 active:scale-[0.98] transition">
                <div className="relative flex-shrink-0">
                  <img src={s.user?.avatar} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#FF2A5F]"/>
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 py-[1px] rounded-full bg-[#FF2A5F] text-white text-[7px] font-extrabold leading-none">LIVE</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold truncate">{s.user?.name || s.user?.username || "host"}</div>
                  <div className="text-[9px] text-white/60 truncate">{s.title || s.category}</div>
                </div>
                <div className="text-[9px] text-white/70 font-bold flex-shrink-0">{(s.viewers || 0)}👀</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Trending Categories cloud — only on right sidebar */}
      {side === "right" && tags.length > 0 && (
        <div className="rounded-2xl bg-gradient-to-br from-[#A855F7]/12 to-black/40 backdrop-blur border border-[#A855F7]/30 p-3" data-testid="sidebar-categories">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#A855F7]/20 border border-[#A855F7]/50">
              <Hash className="w-3.5 h-3.5 text-[#A855F7]"/>
            </div>
            <div className="flex-1">
              <div className="font-outfit font-extrabold text-sm leading-none text-[#A855F7]">Trending</div>
              <div className="text-[9px] text-white/50 mt-0.5">{tags.length} hot topics</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t, i) => (
              <Link key={t} to={`/tag/${encodeURIComponent(t.replace(/^#/, ""))}`}
                data-testid={`sidebar-tag-${i}`}
                className="px-2 py-1 rounded-full bg-white/8 hover:bg-[#A855F7]/25 border border-white/10 hover:border-[#A855F7]/50 text-[10px] font-bold text-white/85 active:scale-95 transition">
                {t.startsWith("#") ? t : `#${t}`}
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
